import { ApiError, api } from '@/lib/api';

/**
 * The Next task, as a REAL TASK that can remind somebody.
 *
 * Owner's request, 10 September 2026: a next task should carry a date and a
 * person, that person should learn they have been given it, and a reminder
 * should reach them on the day.
 *
 * ## Nothing here is new machinery, and that is the point
 *
 * The task engine already does every part of this. A `d_tasks` row attached to
 * a deal shows up in the assignee's **My Tasks**, in the **day brief** bucketed
 * against their own midnight, in the **login reminder**, as a badge on the deal
 * record — and `TaskReminders` in the shell raises a **toast** when it falls
 * due, plus a real browser notification for anybody who has granted permission.
 * Writing the row is the whole feature; none of the surfaces needed touching.
 *
 * The same reasoning the invoice tracker's planned-billing reminders were built
 * on, and the same shape.
 *
 * ## ⚠️ WHAT IT CANNOT DO, SO NOTHING MAY PROMISE IT
 *
 * **Nothing reaches the assignee while EMERGE is closed.** There is no push and
 * no service worker — the owner's standing decision, stated permanently beside
 * the reminder toggle rather than hidden in a tooltip. So "Moin is notified"
 * means: it is waiting for him the moment he next opens the app, and the toast
 * fires on the day **if he has it open**. Copy that implies otherwise is a
 * promise this system does not keep.
 *
 * ## ⚠️ The clock is the ASSIGNEE'S, and every account is on the column default
 *
 * `due_at_utc` is resolved through the timezone of the person the task is
 * assigned to — so 09:00 means 09:00 where THEY are, which is the right rule
 * and the reason the engine stores a wall clock and a zone rather than an
 * instant. **Measured caveat: no staff account has ever had a real timezone
 * set**, so every one resolves against the `Asia/Kolkata` column default. For
 * anybody actually in India that is correct; for anybody else the reminder
 * lands at the wrong hour, silently, and there is nothing on any screen that
 * would say so. It is a known open item, not something this feature introduces.
 */

/** What could not be done, for a caller that reports rather than throws. */
export interface NextTaskReminderFailure {
  /** The person it was meant for, as typed on screen — never an id. */
  assignee: string;
  /** The server's own sentence where there is one. */
  message: string;
}

/**
 * The hour a dated next task falls due.
 *
 * ⚠️ **09:00, NOT MIDNIGHT, and the difference is visible to the user.** The
 * day brief buckets against the caller's own midnight, so a task due at 00:00
 * is overdue the instant its day begins and greets them as ALREADY LATE on the
 * morning it was meant to be a prompt. The invoice tracker's reminders invent
 * a time for the same reason and settled on the same answer; two features
 * inventing two different mornings would be worse than either choice.
 */
const REMINDER_TIME = '09:00';

/**
 * Whether this save should raise a reminder at all.
 *
 * ⚠️ **Gated on `noteText`, which is the value the NOTE rule already produced,
 * and that is what stops a duplicate task on every subsequent save.** That rule
 * compares what is typed against what was LOADED FROM THE RECORD, so re-saving
 * a deal whose next task has not changed yields `''` here and writes nothing.
 * Without it, editing a closing date on a deal that already carries a dated
 * next task would raise another identical task each time — and unlike a
 * duplicated note, a duplicated task pushes a second reminder at a real person.
 *
 * A date or an assignee is required. The bare text on its own is the behaviour
 * that shipped before this feature and stays exactly as it was: a note, and no
 * task.
 */
export function shouldRaiseReminder(
  noteText: string,
  dueDate: string,
  assigneeId: string,
): boolean {
  return noteText !== '' && (dueDate !== '' || assigneeId !== '');
}

/**
 * Creates the task. **Never throws** — it returns what went wrong instead.
 *
 * The deal is already saved by the time this runs, and a rejected promise here
 * would land in the caller's `catch` and be rendered as a FAILED save of a deal
 * that is sitting in the list. That is the same reasoning `writeNextTaskNotes`
 * is built on, and the same reasoning the invoice tracker gives for committing
 * its row before attempting its reminder: a caller who reads "failed" retries,
 * and a retried create is a second deal.
 *
 * ⚠️ **`assignedToId` is OMITTED rather than sent as null when nobody was
 * chosen.** The API defaults it to the caller, which is what makes "a date and
 * no person" mean *remind me* — while an explicit `null` means UNASSIGNED and
 * is a **422 whenever `dueAt` is present**, because a task with no owner has no
 * clock to be expressed on. Two spellings of "nothing", opposite outcomes.
 *
 * ⚠️ `/tasks` is one of the three modules the server enforces by PERMISSION SET
 * rather than by role, so a 403 is reachable for somebody perfectly able to
 * write the deal. The controls are deliberately not gated on it: hiding them
 * would withhold a deal field on the strength of an unrelated module's grant.
 */
export async function createNextTaskReminder(input: {
  dealId: number;
  title: string;
  /** `YYYY-MM-DD` from a date control, or `''` for no due date. */
  dueDate: string;
  /** The chosen user id as a string, or `''` to let the server default to the caller. */
  assigneeId: string;
  /** Only for the failure message, so it names a person rather than an id. */
  assigneeName: string;
}): Promise<NextTaskReminderFailure | null> {
  try {
    await api.createTask({
      title: input.title,
      dealId: input.dealId,
      /*
       * A wall clock with no zone, which is what the engine takes. It is
       * resolved through the assignee's timezone server-side — never parsed
       * into a Date here, which would attach this browser's offset to a value
       * that does not have one.
       */
      ...(input.dueDate !== '' ? { dueAt: `${input.dueDate} ${REMINDER_TIME}` } : {}),
      ...(input.assigneeId !== '' ? { assignedToId: Number(input.assigneeId) } : {}),
    });
    return null;
  } catch (cause) {
    return { assignee: input.assigneeName, message: reminderFailureMessage(cause) };
  }
}

function reminderFailureMessage(cause: unknown): string {
  if (!(cause instanceof ApiError)) {
    return 'The server could not be reached, so the reminder was not created.';
  }
  /*
   * A 403 carries no `details`, so there is nothing per-field to read. Real
   * rather than theoretical: `/tasks` gates on the permission set, and a set
   * can change under an access token that lasts about five days.
   */
  if (cause.status === 403) {
    return 'Your account is not allowed to create tasks.';
  }
  /*
   * `dueAt` and `assignedToId` are the two the server names on this payload —
   * an impossible calendar date, or an assignee it will not accept.
   */
  return (
    cause.fieldErrors.dueAt ??
    cause.fieldErrors.assignedToId ??
    cause.formMessage ??
    cause.message
  );
}
